import { useState } from 'react'

/**
 * UID-610 — the shared Gmail/Finder shift-click range-selection primitive.
 * Extracted from `PartsLibraryPage.tsx`'s `toggleSelect` (UID-470, the first
 * surface to ship this), which is the reference implementation for every
 * consumer wiring into this hook: `PartsTable.tsx`'s row checkbox reads
 * `event.shiftKey` off the `click` event and passes it straight to
 * `toggle` below — a `click` event carries live modifier-key state for both
 * a mouse click AND a Space-key activation of a focused checkbox, so that
 * one `onClick` handler is also the WCAG Shift+Space keyboard equivalent,
 * with no separate keydown wiring needed.
 *
 * `ids` is passed at CALL time (not captured once) so the range is always
 * computed over whatever order the caller is rendering RIGHT NOW — filtering,
 * sorting, or expanding/collapsing a tree between two shift-clicks changes
 * what the range spans, matching the real Finder/Gmail behavior of "select
 * what's visually between the two rows I clicked."
 */
export interface RangeSelection {
  readonly selectedIds: ReadonlySet<string>
  /**
   * `shiftKey` false/omitted flips just `id` and becomes the new anchor —
   * ordinary behavior. A shift-click (`shiftKey` true) with a live anchor
   * applies the RESULT of toggling `id` (select if unchecked, deselect if
   * checked) to every id in `ids` between the anchor and `id`, inclusive,
   * then advances the anchor to `id`. If the anchor is no longer present in
   * `ids` (e.g. it scrolled out of the current filter), this falls back to
   * a plain toggle rather than computing a meaningless range.
   */
  readonly toggle: (id: string, ids: readonly string[], shiftKey?: boolean) => void
  readonly toggleAll: (ids: readonly string[], select: boolean) => void
  /** Wipes the selection AND the anchor — see `clear`'s call sites for why
   * a stale anchor left behind a wipe must not survive it: a later
   * shift-click could otherwise resurrect a range from a row the user no
   * longer sees as selected. */
  readonly clear: () => void
  /** Escape hatch for callers that need to seed or restore selection state
   * directly (e.g. re-selecting rows after an edit) without going through
   * `toggle`/`toggleAll`. Does not touch the anchor. */
  readonly setSelectedIds: (next: ReadonlySet<string>) => void
}

export function useRangeSelection(): RangeSelection {
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set())
  const [anchorId, setAnchorId] = useState<string | null>(null)

  function toggle(id: string, ids: readonly string[], shiftKey = false) {
    if (shiftKey && anchorId !== null) {
      const anchorIndex = ids.indexOf(anchorId)
      const targetIndex = ids.indexOf(id)
      if (anchorIndex !== -1 && targetIndex !== -1) {
        const start = Math.min(anchorIndex, targetIndex)
        const end = Math.max(anchorIndex, targetIndex)
        const rangeIds = ids.slice(start, end + 1)
        const willSelect = !selectedIds.has(id)
        setSelectedIds((prev) => {
          const next = new Set(prev)
          for (const rangeId of rangeIds) {
            if (willSelect) next.add(rangeId)
            else next.delete(rangeId)
          }
          return next
        })
        setAnchorId(id)
        return
      }
      // Anchor no longer in the currently-rendered order — fall through to
      // a plain toggle below rather than computing a meaningless range.
    }
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
    setAnchorId(id)
  }

  function toggleAll(ids: readonly string[], select: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      for (const id of ids) {
        if (select) next.add(id)
        else next.delete(id)
      }
      return next
    })
  }

  function clear() {
    setSelectedIds(new Set())
    setAnchorId(null)
  }

  return { selectedIds, toggle, toggleAll, clear, setSelectedIds }
}
